import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { plural } from "../../../../features/analytics/profileFindingsModel";
import type { GbpCategoryOption, GbpDay, GbpHoursPeriod, GbpOpeningDate, GbpPhotoCategory } from "../../../../services/gbpService";
import { GBP_PHOTO_CATEGORIES } from "../../../../services/gbpConstants";
import type { MediaItem } from "../../../../types/MediaItem";
import { getMediaThumbnailUrl } from "../../../../types/MediaItem";
import { GbpLoadingCard } from "./GbpLoadingCard";
import { MediaLibraryModal } from "./MediaLibraryModal";
import type {
  ApplyItem,
  AskRow,
  AttributeRow,
  EditOutcome,
  FixValue,
  GbpPhotoPage,
  GbpProposalAttachment,
  HandOffRow,
  PhotoRow,
  PrimaryRow,
  ServiceRow,
} from "../utils/gbpProposalText";
import {
  GBP_DAYS,
  SOURCE_LABELS,
  applicableItems,
  attributeValueLabel,
  auditProblem,
  canRetry,
  formatValue,
  gbpConnectLabel,
  handOffExplain,
  hasAnything,
  hoursRows,
  isHours,
  isSettled,
  isWaiting,
  liveProgress,
  NO_ANSWER_MESSAGE,
  noProposalMessage,
  parseList,
  photoCategoryLabel,
  readSections,
} from "../utils/gbpProposalText";

/**
 * The Visibility Manager proposing Google Business Profile fixes in its chat
 * (SGL-328 Phase B/C). Rendered inside the agent's reply when the owner asks
 * "What's holding back my Google Business Profile?".
 *
 * One card at a time, in a sliding deck: first Google's own automatic updates
 * (AC11; nothing moves past it until the owner confirms), then one card per fix
 * with what is on the profile now and what it changes to, then what only the
 * owner can add, the team's work, and a summary of what reached Google.
 *
 * Every fix is proposed, never guessed: a missing service comes from the lost
 * searches, an empty field from a real source (Brand details, Google's category
 * list, a description written from Brand details), and a field with no source
 * waits for the owner. Add sends that one card's change as its own edit (AC7).
 * The card then shows what Google did: live, being reviewed (AC9), or not
 * changed with the reason (AC10), next to what it was before (AC8).
 *
 * Presentational: the card never calls an API. Changes go up through onChange,
 * approvals through onApply, and results come back on `proposal`.
 */

const CATEGORY_SEARCH_DEBOUNCE_MS = 300;
/** Space between slides on the track, in px. */
const SLIDE_GAP = 24;
const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const SLIDE_MS = 550;

const BTN_PRIMARY =
  "h-10 rounded-[10px] bg-theme-primary px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-theme-primary-hover disabled:cursor-not-allowed disabled:opacity-45";
const BTN_SECONDARY =
  "h-10 rounded-[10px] border border-theme-border bg-theme-surface px-4 text-[13.5px] font-semibold text-theme-fg transition-colors hover:border-theme-fg-muted disabled:cursor-not-allowed disabled:opacity-45";
const BTN_QUIET =
  "h-10 rounded-[10px] px-3 text-[13.5px] font-medium text-theme-fg-muted transition-colors hover:text-theme-fg disabled:opacity-45";
const INPUT =
  "w-full rounded-[10px] border border-theme-border bg-theme-surface px-3 py-2.5 text-[14px] text-theme-fg placeholder:text-theme-fg-muted focus:border-theme-fg-muted focus:outline-none focus:ring-0";
/* A compact select for a table row. No fixed height: a forced height plus INPUT's
   padding left too little room and clipped the chosen option's text. */
const SELECT_SMALL =
  "w-auto min-w-[150px] rounded-[10px] border border-theme-border bg-theme-surface py-1.5 pl-3 pr-9 text-[13.5px] leading-6 text-theme-fg focus:border-theme-fg-muted focus:outline-none focus:ring-0";
const CARD = "rounded-[14px] border border-theme-border bg-theme-surface px-6 pb-5 pt-6 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-18px_rgba(16,24,40,0.22)] sm:px-7";
const TABLE = "overflow-hidden rounded-[10px] border border-theme-border";
const KICKER = "text-[12.5px] tabular-nums text-theme-fg-muted";
const TITLE = "text-[20px] font-semibold leading-snug tracking-[-0.015em] text-theme-fg [text-wrap:balance]";
const EXPLAIN = "mt-1.5 max-w-[58ch] text-[14px] leading-relaxed text-theme-fg-muted";

export interface GbpFixProposalProps {
  proposal: GbpProposalAttachment;
  /** The name the owner gave this agent; labels the card for screen readers. */
  agentName?: string;
  /** Inside a chat reply the thread already shows who is speaking: no own avatar. */
  embedded?: boolean;
  /** The owner changed the card (edited a value, answered, dismissed). */
  onChange?: (next: GbpProposalAttachment) => void;
  /** The owner approved these rows; each is one edit. */
  onApply?: (items: ApplyItem[]) => void;
  /** Open another agent's chat with this message ready to send. */
  onHandOff?: (agentId: string, prompt: string) => void;
  /** Google's own categories matching a query (for editing categories). */
  searchCategories?: (query: string) => Promise<GbpCategoryOption[]>;
  /** One page of the owner's Media Library images that may go on the profile (photo
   *  card). Pass the previous page's nextToken for the next one. */
  loadMediaLibrary?: (nextToken?: string | null) => Promise<GbpPhotoPage>;
  /** Take the owner to where they connect Google. Offered only when the card could not
   *  read the profile because it isn't connected (or the connection expired). */
  onConnect?: () => void;
  /** Re-check applied changes on Google (`all` includes ones already live). False if Google couldn't be asked. */
  checkStatus?: (all?: boolean) => Promise<boolean>;
}

/** Re-checks while the card shows changes waiting on Google: soon after a send,
 *  then every half minute for about 20 minutes. "Check again" works any time. */
const STATUS_FIRST_MS = 5_000;
const STATUS_POLL_MS = 30_000;
const STATUS_POLL_MAX = 40;

/** "just now" / "3 min ago" / "2 h ago" for the last check. */
function checkedAgo(iso: string | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const mins = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(mins)) return null;
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} h ago`;
}

const domId = (key: string) => `gbp-fix-${key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;

/* ── words ─────────────────────────────────────────────────────────────── */

/** One plain line under each fix's title: what is wrong, in the owner's terms. */
const EXPLAIN_BY_GAP: Record<string, string> = {
  no_service_items: "Your profile lists no services, so Google can't match you to what people search for.",
  no_additional_categories: "Categories tell Google what kind of business you are. You're only listed under your main one.",
  empty_description: "Your profile has no short paragraph telling customers what your business does.",
  no_website: "Your profile has no website link, so customers can't reach your site from Google.",
  no_phone: "Your profile has no phone number, so customers can't call you from Google.",
  no_regular_hours: "Your profile has no opening hours, so customers can't tell when you're open.",
  no_opening_date: "Your profile doesn't say when your business opened. Google shows it to customers.",
};

/** The "Now" cell. The audit only reports that a field is empty, never its value. */
const NOW_BY_GAP: Record<string, string> = {
  no_additional_categories: "Only your main category",
};
const NOW_MISSING = "Missing from your profile";

/** Where on Google the owner sets a field Sagelyn can't write yet. */
const WHERE_BY_GAP: Record<string, string> = {
  no_service_area: "Edit profile → Location",
};

const DAY_NAMES: Record<GbpDay, string> = {
  MONDAY: "Monday", TUESDAY: "Tuesday", WEDNESDAY: "Wednesday", THURSDAY: "Thursday",
  FRIDAY: "Friday", SATURDAY: "Saturday", SUNDAY: "Sunday",
};
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** Google accepts an opening date up to a year ahead. */
const OPENING_YEAR_MIN = 1800;

/* ── the slides ────────────────────────────────────────────────────────── */

type Slide =
  | { kind: "setup"; key: string }
  | { kind: "service"; key: string; row: ServiceRow }
  | { kind: "ask"; key: string; row: AskRow }
  | { kind: "attributes"; key: string; rows: AttributeRow[] }
  | { kind: "photo"; key: string; row: PhotoRow }
  | { kind: "primary"; key: string; row: PrimaryRow }
  | { kind: "manual"; key: string; rows: AskRow[] }
  | { kind: "team"; key: string; rows: HandOffRow[] }
  | { kind: "summary"; key: string };

const isFix = (s: Slide): s is Extract<Slide, { kind: "service" | "ask" }> => s.kind === "service" || s.kind === "ask";

/* ── small pieces ──────────────────────────────────────────────────────── */

export const Dot: React.FC<{ tone: "live" | "review" | "failed" | "none" }> = ({ tone }) => (
  <span
    aria-hidden="true"
    className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${
      tone === "live" ? "bg-theme-primary" : tone === "review" ? "bg-theme-accent-warm" : tone === "failed" ? "bg-theme-danger" : "bg-theme-border"
    }`}
  />
);

/** What Google did with this card's change, in words the owner can act on. */
export const OutcomeLine: React.FC<{ outcome: EditOutcome | null }> = ({ outcome }) => {
  if (!outcome) return null;
  if (outcome.status === "applying") {
    return (
      <p role="status" className="flex items-center gap-2.5 text-[14px] text-theme-fg-muted">
        <span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-theme-border border-t-theme-fg" />
        Sending to Google…
      </p>
    );
  }
  if (outcome.status === "live") {
    return (
      <p role="status" className="flex items-center gap-2.5 text-[14px] text-theme-fg">
        <Dot tone="live" />
        Live on Google
      </p>
    );
  }
  if (outcome.status === "pending_review") {
    return (
      <p role="status" className="flex items-baseline gap-2.5 text-[14px] text-theme-fg">
        <Dot tone="review" />
        <span>
          Waiting for Google to publish it.{" "}
          <span className="text-theme-fg-muted">This usually takes a few hours to a few days.</span>
        </span>
      </p>
    );
  }
  if (outcome.status === "applied_unverified") {
    // Google took the write but we could not read it back: sent, not confirmed.
    return (
      <p role="status" className="flex items-baseline gap-2.5 text-[14px] text-theme-fg">
        <Dot tone="review" />
        <span>{outcome.message || UNVERIFIED_FALLBACK}</span>
      </p>
    );
  }
  if (outcome.status === "submitted") {
    return (
      <p role="status" className="flex items-baseline gap-2.5 text-[14px] text-theme-fg">
        <Dot tone="review" />
        <span>
          Sent to Google. <span className="text-theme-fg-muted">It can take a little while to appear on your profile.</span>
        </span>
      </p>
    );
  }
  if (outcome.status === "no_answer") {
    // The request went out and nothing usable came back: it may be on Google.
    return (
      <p role="status" className="flex items-baseline gap-2.5 text-[14px] text-theme-fg">
        <Dot tone="review" />
        <span>{outcome.message || NO_ANSWER_MESSAGE}</span>
      </p>
    );
  }
  if (outcome.status === "not_on_google") {
    return (
      <p role="status" className="flex items-baseline gap-2.5 text-[14px] text-theme-fg">
        <Dot tone="failed" />
        <span>
          Not showing on Google.{" "}
          <span className="text-theme-fg-muted">Google may have changed it back or not accepted it. You can try again.</span>
        </span>
      </p>
    );
  }
  return (
    <p role="status" className="flex items-baseline gap-2.5 text-[14px] text-theme-fg">
      <Dot tone="failed" />
      <span>
        Not changed. <span className="text-theme-fg-muted">{outcome.message}</span>
      </span>
    </p>
  );
};

const UNVERIFIED_FALLBACK = "Sent to Google, but I couldn't confirm it yet. I'll check again on the next visibility check.";

/** Something to know although the change went through (e.g. Google dropped services). */
export const WarningLine: React.FC<{ outcome: EditOutcome | null }> = ({ outcome }) =>
  outcome?.warning ? <p className="text-[13.5px] leading-relaxed text-theme-fg">{outcome.warning}</p> : null;

/** A change's status as one word for the summary. Never "Live" until Google says so. */
export function statusWord(o: EditOutcome | null | undefined): ["live" | "review" | "failed" | "none", string] {
  switch (o?.status) {
    case "live": return ["live", "Live on Google"];
    case "pending_review": return ["review", "Waiting for Google"];
    case "applied_unverified": return ["review", "Not confirmed yet"];
    case "submitted": return ["review", "Sent to Google"];
    case "no_answer": return ["review", "No answer yet"];
    case "not_on_google": return ["failed", "Not on Google"];
    case "failed": return ["failed", "Not changed"];
    case "applying": return ["none", "Sending…"];
    default: return ["none", "Not changed"];
  }
}

/* ── editors ───────────────────────────────────────────────────────────── */

const EditorShell: React.FC<{
  children: React.ReactNode;
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
  footnote?: React.ReactNode;
}> = ({ children, canSave, onSave, onCancel, footnote }) => (
  <div className="space-y-2.5">
    {children}
    <div className="flex items-center gap-2">
      <button type="button" disabled={!canSave} onClick={onSave} className={BTN_PRIMARY}>
        Save
      </button>
      <button type="button" onClick={onCancel} className={BTN_QUIET}>
        Cancel
      </button>
      <span className="flex-1 text-right text-[12px] text-theme-fg-muted">{footnote}</span>
    </div>
  </div>
);

const TextEditor: React.FC<{ row: AskRow; onSave: (v: FixValue) => void; onCancel: () => void }> = ({
  row,
  onSave,
  onCancel,
}) => {
  const list = row.spec.kind === "list";
  const [draft, setDraft] = useState(row.value ? formatValue(row.value) : "");
  const id = `${domId(row.key)}-input`;
  const value: FixValue | null = list ? (parseList(draft).length ? parseList(draft) : null) : draft.trim() || null;
  const props = {
    id,
    value: draft,
    maxLength: row.spec.maxLength,
    placeholder: row.spec.placeholder,
    "aria-label": row.spec.title,
    autoFocus: true,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    className: INPUT,
  };
  return (
    <EditorShell
      canSave={!!value}
      onSave={() => value && onSave(value)}
      onCancel={onCancel}
      footnote={row.spec.kind === "multiline" ? `${draft.length} / ${row.spec.maxLength}` : list ? "Separate with commas" : undefined}
    >
      {row.spec.kind === "multiline" ? <textarea rows={6} {...props} /> : <input type="text" {...props} />}
    </EditorShell>
  );
};

export const CategoryEditor: React.FC<{
  row: Pick<AskRow, "value" | "spec">;
  search?: (q: string) => Promise<GbpCategoryOption[]>;
  onSave: (v: FixValue) => void;
  onCancel: () => void;
  /** Exactly one category (the main one): a new pick replaces the old. */
  single?: boolean;
}> = ({ row, search, onSave, onCancel, single = false }) => {
  const [picked, setPicked] = useState<GbpCategoryOption[]>(Array.isArray(row.value) ? (row.value as GbpCategoryOption[]) : []);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GbpCategoryOption[]>([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!search || q.length < 2) {
      setResults([]);
      return;
    }
    const mine = ++seq.current;
    const t = window.setTimeout(() => {
      setSearching(true);
      search(q)
        .then((found) => mine === seq.current && setResults(found))
        .catch(() => mine === seq.current && setResults([]))
        .finally(() => mine === seq.current && setSearching(false));
    }, CATEGORY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, search]);

  const add = (c: GbpCategoryOption) => {
    if (single) setPicked([c]);
    else if (!picked.some((p) => p.name === c.name)) setPicked([...picked, c]);
    setQuery("");
    setResults([]);
  };

  return (
    <EditorShell canSave={picked.length > 0} onSave={() => onSave(picked)} onCancel={onCancel} footnote="Only Google's own categories">
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {picked.map((c) => (
            <span key={c.name} className="flex items-center gap-1 rounded-full border border-theme-border py-1 pl-3 pr-1 text-[13px] font-medium text-theme-fg">
              {c.display_name}
              <button
                type="button"
                aria-label={`Remove ${c.display_name}`}
                onClick={() => setPicked(picked.filter((p) => p.name !== c.name))}
                className="flex h-5 w-5 items-center justify-center rounded-full text-theme-fg-muted transition-colors hover:text-theme-fg"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {search && (
        <div>
          <input
            type="text"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            placeholder={row.spec.placeholder}
            aria-label="Search Google's categories"
            className={INPUT}
          />
          {(results.length > 0 || searching) && (
            <ul className="mt-1.5 max-h-52 overflow-auto rounded-[10px] border border-theme-border bg-theme-surface p-1">
              {searching && <li className="px-3 py-2 text-[13px] text-theme-fg-muted">Searching…</li>}
              {results.map((c) => (
                <li key={c.name}>
                  <button
                    type="button"
                    onClick={() => add(c)}
                    className="w-full rounded-lg px-3 py-2 text-left text-[13.5px] text-theme-fg transition-colors hover:bg-theme-background"
                  >
                    {c.display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </EditorShell>
  );
};

/** One row per day: open or closed, and if open, from and until. One period a
 *  day covers almost every shop; the backend accepts more for later. */
export const HoursEditor: React.FC<{ row: Pick<AskRow, "value">; onSave: (v: FixValue) => void; onCancel: () => void }> = ({
  row,
  onSave,
  onCancel,
}) => {
  const start = isHours(row.value) ? row.value : [];
  const [week, setWeek] = useState<Record<GbpDay, { open: boolean; from: string; to: string }>>(() => {
    const out = {} as Record<GbpDay, { open: boolean; from: string; to: string }>;
    for (const day of GBP_DAYS) {
      const p = start.find((x) => x.day === day);
      // "24:00" (end of day) has no <input type="time"> form; 23:59 is the same close.
      out[day] = { open: !!p, from: p?.open ?? "09:00", to: p ? (p.close === "24:00" ? "23:59" : p.close) : "18:00" };
    }
    return out;
  });
  const set = (day: GbpDay, patch: Partial<{ open: boolean; from: string; to: string }>) =>
    setWeek((w) => ({ ...w, [day]: { ...w[day], ...patch } }));

  const badDays = GBP_DAYS.filter((d) => week[d].open && !(week[d].from && week[d].to && week[d].to > week[d].from));
  const periods: GbpHoursPeriod[] = GBP_DAYS.filter((d) => week[d].open).map((day) => ({ day, open: week[day].from, close: week[day].to }));
  const footnote = badDays.length
    ? `${DAY_NAMES[badDays[0]]}: closing time must be after opening time`
    : periods.length === 0
      ? "Open at least one day"
      : undefined;

  return (
    <EditorShell canSave={periods.length > 0 && badDays.length === 0} onSave={() => onSave(periods)} onCancel={onCancel} footnote={footnote}>
      <ul className="divide-y divide-theme-border rounded-[10px] border border-theme-border">
        {GBP_DAYS.map((day) => {
          const d = week[day];
          return (
            <li key={day} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
              <label className="flex w-32 cursor-pointer items-center gap-2.5 text-[14px] font-normal text-theme-fg">
                <input
                  type="checkbox"
                  checked={d.open}
                  onChange={(e) => set(day, { open: e.target.checked })}
                  aria-label={`Open on ${DAY_NAMES[day]}`}
                  className="h-4 w-4 cursor-pointer rounded border-theme-border text-theme-primary focus:ring-0 focus:ring-offset-0"
                />
                {DAY_NAMES[day]}
              </label>
              {d.open ? (
                <span className="flex items-center gap-2 text-[13px] text-theme-fg-muted">
                  <input
                    type="time"
                    value={d.from}
                    onChange={(e) => set(day, { from: e.target.value })}
                    aria-label={`${DAY_NAMES[day]} opens`}
                    className={`${INPUT} w-[120px] py-1.5`}
                  />
                  to
                  <input
                    type="time"
                    value={d.to}
                    onChange={(e) => set(day, { to: e.target.value })}
                    aria-label={`${DAY_NAMES[day]} closes`}
                    className={`${INPUT} w-[120px] py-1.5`}
                  />
                </span>
              ) : (
                <span className="text-[13px] text-theme-fg-muted">Closed</span>
              )}
            </li>
          );
        })}
      </ul>
    </EditorShell>
  );
};

/** Month and year: Google accepts an opening date without the day. */
export const DateEditor: React.FC<{ row: Pick<AskRow, "value">; onSave: (v: FixValue) => void; onCancel: () => void }> = ({ row, onSave, onCancel }) => {
  const now = new Date();
  const maxYear = now.getFullYear() + 1;
  const start = row.value && !Array.isArray(row.value) && typeof row.value === "object" ? (row.value as GbpOpeningDate) : null;
  const [month, setMonth] = useState(start ? String(start.month) : "");
  const [year, setYear] = useState(start ? String(start.year) : "");
  const y = Number(year);
  const m = Number(month);
  const validYear = /^\d{4}$/.test(year) && y >= OPENING_YEAR_MIN && y <= maxYear;
  // Google: no more than a year ahead.
  const tooFar = validYear && m > 0 && new Date(y, m - 1, 1) > new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
  const canSave = m >= 1 && m <= 12 && validYear && !tooFar;
  return (
    <EditorShell
      canSave={canSave}
      onSave={() => canSave && onSave({ year: y, month: m })}
      onCancel={onCancel}
      footnote={tooFar ? "Google accepts a date up to a year ahead" : year && !validYear ? `Enter a year between ${OPENING_YEAR_MIN} and ${maxYear}` : undefined}
    >
      <div className="flex flex-wrap gap-2">
        <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" className={`${INPUT} w-auto min-w-[160px] font-normal`}>
          <option value="">Month</option>
          {MONTH_NAMES.map((name, i) => (
            <option key={name} value={i + 1}>
              {name}
            </option>
          ))}
        </select>
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          placeholder="Year"
          value={year}
          onChange={(e) => setYear(e.target.value.replace(/\D/g, ""))}
          aria-label="Year"
          className={`${INPUT} w-[110px] font-normal`}
        />
      </div>
    </EditorShell>
  );
};

/* ── the card ──────────────────────────────────────────────────────────── */

/** The photo card's Media Library picker: one page at a time, newest first. */
interface LibraryState {
  open: boolean;
  loading: boolean;
  loadingMore: boolean;
  items: MediaItem[];
  /** Where the next page starts; null when there is no more. */
  nextToken: string | null;
  /** The first page failed; shown on the card, the picker stays closed. */
  failed: boolean;
  /** "Load more" failed; shown in the picker, which stays open to retry. */
  moreFailed: boolean;
}

const LIBRARY_CLOSED: LibraryState = {
  open: false,
  loading: false,
  loadingMore: false,
  items: [],
  nextToken: null,
  failed: false,
  moreFailed: false,
};

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

export const GbpFixProposal: React.FC<GbpFixProposalProps> = ({
  proposal,
  agentName,
  embedded = false,
  onChange,
  onApply,
  onHandOff,
  searchCategories,
  loadMediaLibrary,
  onConnect,
  checkStatus,
}) => {
  // The card's state lives on the message (so it is stored and reloads); this
  // copy keeps the card usable when nobody listens, e.g. in isolation.
  const [card, setCard] = useState(proposal);
  useEffect(() => setCard(proposal), [proposal]);
  const update = (next: GbpProposalAttachment) => {
    setCard(next);
    onChange?.(next);
  };

  const [editing, setEditing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The Media Library picker for the photo card. Each open bumps librarySeq, so a
  // page that lands after the picker was closed or reopened is dropped.
  const [library, setLibrary] = useState<LibraryState>(LIBRARY_CLOSED);
  const librarySeq = useRef(0);
  // Reopened from History with the question answered: start on the first fix.
  const [index, setIndex] = useState(() => (proposal.googleAutoOff ? 1 : 0));

  const sections = useMemo(() => readSections(card), [card]);
  const items = useMemo(() => applicableItems(sections), [sections]);
  const { services, lost, ask, handOffs, notes, attributes, attributesNotEditable, photo, primary } = sections;

  const outcomes = Object.values(card.applied ?? {});
  const busy = outcomes.some((o) => o.status === "applying");
  const sent = outcomes.filter(isSettled).length;
  // Sent with no answer back: may be on Google, so "nothing changed" would be a claim.
  const unanswered = outcomes.some((o) => o.status === "no_answer");
  const progress = liveProgress(card.applied);
  const allLive = progress.total > 0 && progress.live === progress.total;
  // Only rows Google never answered: "no answer" says more than "0 of N live".
  const onlyUnanswered = progress.total > 0 && progress.noAnswer === progress.total;
  const lastChecked = outcomes.reduce<string | undefined>((a, o) => (o.checkedAt && (!a || o.checkedAt > a) ? o.checkedAt : a), undefined);
  const autoOff = !!card.googleAutoOff;

  // Live status: re-check changes still waiting on Google while the card is open.
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const checkRef = useRef(checkStatus);
  checkRef.current = checkStatus;
  const mounted = useRef(true);
  useEffect(() => () => void (mounted.current = false), []);
  const runCheck = useCallback(async (all = false) => {
    if (!checkRef.current) return;
    setChecking(true);
    const ok = await checkRef.current(all);
    if (!mounted.current) return;
    setCheckFailed(!ok);
    setChecking(false);
  }, []);
  const hasWaiting = outcomes.some((o) => isWaiting(o) && !!o.check);
  useEffect(() => {
    if (!checkStatus || !hasWaiting || card.loading) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      tries += 1;
      await runCheck();
      if (mounted.current && tries < STATUS_POLL_MAX) timer = setTimeout(tick, STATUS_POLL_MS);
    };
    timer = setTimeout(tick, STATUS_FIRST_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWaiting, card.loading, !!checkStatus, runCheck]);
  // Anything Sagelyn could write, sendable yet or not (typical hours wait for the
  // owner): the deck's shape must not change when a value is confirmed.
  const canManage =
    services.length > 0 || ask.some((a) => a.appliable) || attributes.length > 0 || !!photo || !!primary || sent > 0;

  const slides = useMemo<Slide[]>(() => {
    const out: Slide[] = [];
    const appliable = ask.filter((a) => a.appliable);
    const manual = ask.filter((a) => !a.appliable);
    if (canManage) out.push({ kind: "setup", key: "setup" });
    services.forEach((row) => out.push({ kind: "service", key: row.key, row }));
    appliable.forEach((row) => out.push({ kind: "ask", key: row.key, row }));
    if (photo) out.push({ kind: "photo", key: photo.key, row: photo });
    if (attributes.length) out.push({ kind: "attributes", key: "attributes", rows: attributes });
    // Optional, never a claimed problem: after everything the profile is missing.
    if (primary) out.push({ kind: "primary", key: primary.key, row: primary });
    if (manual.length) out.push({ kind: "manual", key: "manual", rows: manual });
    if (handOffs.length) out.push({ kind: "team", key: "team", rows: handOffs });
    if (services.length + appliable.length > 0 || notes.length || photo || attributes.length || primary) {
      out.push({ kind: "summary", key: "summary" });
    }
    return out;
  }, [ask, services, handOffs, notes, photo, attributes, primary, canManage]);

  const fixes = slides.filter(isFix);
  // What the lead counts as "things to fix": the missing ones. The main category
  // is optional, so it is not counted.
  const fixCount = fixes.length + (photo ? 1 : 0) + (attributes.length ? 1 : 0);
  const at = Math.max(0, Math.min(index, slides.length - 1));
  // AC11: nothing past the first card until Google's automatic updates are off.
  const gated = slides[0]?.kind === "setup" && !autoOff;
  const maxIndex = gated ? 0 : slides.length - 1;

  const go = useCallback(
    (to: number) => {
      setEditing(null);
      setIndex(Math.max(0, Math.min(to, maxIndex)));
    },
    [maxIndex],
  );

  /* The track slides; the viewport eases to the active card's height. */
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = slideRefs.current[at];
    if (!el) return;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [at, slides.length]);
  useEffect(() => {
    slideRefs.current.forEach((el, k) => {
      if (!el) return;
      if (k === at) el.removeAttribute("inert");
      else el.setAttribute("inert", "");
    });
  });

  const touchX = useRef<number | null>(null);

  /* Once, when the audit lands: bring the card's top into view. The chat no
     longer follows in-place card updates, so without this a card taller than
     the space left below would open cut off. Not on reload from History. */
  const rootRef = useRef<HTMLElement>(null);
  const wasLoading = useRef(card.loading);
  useEffect(() => {
    if (wasLoading.current && !card.loading) {
      rootRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
    wasLoading.current = card.loading;
  }, [card.loading]);

  if (card.dismissed) return null;

  const initial = ((agentName || "").trim()[0] || "A").toUpperCase();
  const label = agentName ? `${agentName}: Business Profile fixes` : "Business Profile fixes";
  const avatar = embedded ? null : (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-theme-primary text-[13px] font-bold text-white"
    >
      {initial}
    </span>
  );

  if (card.loading) {
    return (
      <section aria-label={label} className="flex w-full gap-3">
        {avatar}
        <GbpLoadingCard quiet={!!card.askedInChat} />
      </section>
    );
  }

  if (!hasAnything(sections)) {
    const message = [noProposalMessage(card.findings, card.failed), auditProblem(card.audit)].filter(Boolean).join(" ");
    const connectLabel = gbpConnectLabel(card);
    return (
      <section ref={rootRef} aria-label={label} className="flex w-full scroll-mt-6 gap-3 text-left">
        {avatar}
        <div className="min-w-0 flex-1">
          <p role="status" className="pt-1 text-[14px] leading-relaxed text-theme-fg">
            {message}
          </p>
          {connectLabel && onConnect && (
            <button type="button" onClick={onConnect} className={`${BTN_SECONDARY} mt-3`}>
              {connectLabel}
            </button>
          )}
        </div>
      </section>
    );
  }

  const itemFor = (key: string) => items.find((i) => i.key === key);
  const saveValue = (code: string, value: FixValue) => {
    update({ ...card, answers: { ...(card.answers ?? {}), [code]: value } });
    setEditing(null);
  };
  const toggleExpanded = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  const lead =
    fixCount === 0 ? (
      <>Here's your Google Business Profile and what I found.</>
    ) : (
      <>
        Here's your Google Business Profile.{" "}
        {services.length > 0 && lost > 0
          ? `I checked it against ${plural(lost, "local search", "local searches")} you lost and found `
          : "I found "}
        <strong className="font-semibold">{plural(fixCount, "thing to fix", "things to fix")}</strong>.{" "}
        {fixCount === 1 ? "We'll go through it together." : "We'll go through them one at a time."} Nothing changes on
        Google until you press Add.
      </>
    );

  /* ── one fix ── */

  /* ── moving through the deck: Back and Next/Skip sit together at the bottom right ── */

  // Never locked while an edit is sending: moving between cards doesn't touch it.
  const back = () =>
    at > 0 ? (
      <button type="button" onClick={() => go(at - 1)} className={BTN_QUIET}>
        Back
      </button>
    ) : null;

  /** The bottom row of a card with nothing to apply: Back, then Next. */
  const navRow = () =>
    at > 0 || at < slides.length - 1 ? (
      <div className="flex items-center gap-2">
        <span className="flex-1" />
        {back()}
        {at < slides.length - 1 && (
          <button type="button" onClick={() => go(at + 1)} className={BTN_SECONDARY}>
            Next
          </button>
        )}
      </div>
    ) : null;

  const renderFix = (s: Extract<Slide, { kind: "service" | "ask" }>) => {
    const n = fixes.indexOf(s) + 1;
    const isService = s.kind === "service";
    const outcome = s.row.outcome;
    const settled = isSettled(outcome);
    const applying = outcome?.status === "applying";
    const item = itemFor(s.key);
    const title = isService ? "Add a service" : s.row.spec.title;
    const explain = isService
      ? `${s.row.group.service} isn't listed on your profile, so Google won't show you to people looking for it.`
      : EXPLAIN_BY_GAP[s.row.code] ?? s.row.detail;
    const before = outcome?.old != null && formatValue(outcome.old)
      ? formatValue(outcome.old)
      : isService
        ? "Not listed on your profile"
        : NOW_BY_GAP[s.row.code] ?? NOW_MISSING;
    const value: FixValue | null = isService ? s.row.group.service : s.row.value;
    const source = isService ? "lost_searches" : s.row.source;
    const long = !isService && s.row.spec.kind === "multiline";
    const open = expanded.has(s.key);
    const isEditing = !isService && editing === s.row.code;
    const loadingSuggestion = !isService && !value && !!card.suggestionsLoading;
    const headingId = `${domId(s.key)}-title`;

    let newCell: React.ReactNode;
    if (isEditing && !isService) {
      const editorProps = { row: s.row, onSave: (v: FixValue) => saveValue(s.row.code, v), onCancel: () => setEditing(null) };
      newCell =
        s.row.spec.kind === "categories" ? (
          <CategoryEditor {...editorProps} search={searchCategories} />
        ) : s.row.spec.kind === "hours" ? (
          <HoursEditor {...editorProps} />
        ) : s.row.spec.kind === "date" ? (
          <DateEditor {...editorProps} />
        ) : (
          <TextEditor {...editorProps} />
        );
    } else if (value) {
      newCell = (
        <>
          {isHours(value) ? (
            <span className={`grid grid-cols-[76px_minmax(0,1fr)] gap-x-3 gap-y-0.5 tabular-nums ${source === "typical" ? "text-theme-fg-muted" : ""}`}>
              {hoursRows(value).map((r) => (
                <React.Fragment key={r.days}>
                  <span>{r.days}</span>
                  <span>{r.hours}</span>
                </React.Fragment>
              ))}
            </span>
          ) : (
            <span className={long ? `text-[14.5px] font-normal leading-[1.65] ${open ? "" : "line-clamp-3"}` : "break-words"}>
              {formatValue(value)}
            </span>
          )}
          {long && (
            <button
              type="button"
              onClick={() => toggleExpanded(s.key)}
              className="justify-self-start text-[13px] font-medium text-theme-fg underline decoration-theme-border underline-offset-[3px] hover:decoration-theme-fg"
            >
              {open ? "Show less" : "Read all"}
            </button>
          )}
          {source && <span className="text-[12.5px] font-normal text-theme-fg-muted">{SOURCE_LABELS[source]}</span>}
        </>
      );
    } else {
      newCell = (
        <span className={`font-normal text-theme-fg-muted ${loadingSuggestion ? "animate-pulse" : ""}`}>
          {loadingSuggestion ? "Finding a suggestion…" : "I don't have a value for this. Add yours."}
        </span>
      );
    }

    return (
      <div className={`${CARD} grid gap-5`} aria-labelledby={headingId}>
        <div>
          <p className={KICKER}>
            Fix {n} of {fixes.length}
          </p>
          <h3 id={headingId} className={`mt-1 ${TITLE}`}>
            {title}
          </h3>
          {explain && <p className={EXPLAIN}>{explain}</p>}
        </div>

        <dl className={TABLE}>
          <div className="grid gap-1.5 px-4 py-3.5 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-4 sm:px-[18px] sm:py-4">
            <dt className="text-[13px] text-theme-fg-muted">Now</dt>
            <dd className="min-w-0 break-words text-[14.5px] text-theme-fg-muted">{before}</dd>
          </div>
          <div className="grid gap-1.5 border-t border-theme-border px-4 py-3.5 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-4 sm:px-[18px] sm:py-4">
            <dt className="text-[13px] font-semibold text-theme-fg">{outcome?.status === "live" ? "On Google" : "Change to"}</dt>
            <dd className="grid min-w-0 gap-1.5 text-[14.5px] font-medium text-theme-fg">{newCell}</dd>
          </div>
        </dl>

        <OutcomeLine outcome={outcome} />
        <WarningLine outcome={outcome} />

        {settled ? (
          <div className="flex items-center">
            <span className="flex-1" />
            {back()}
            {at < slides.length - 1 && (
              <button type="button" onClick={() => go(at + 1)} className={BTN_SECONDARY}>
                Next
              </button>
            )}
          </div>
        ) : !isEditing ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!item || busy || !autoOff || !onApply}
              onClick={() => item && onApply?.([item])}
              className={BTN_PRIMARY}
            >
              {applying ? "Sending…" : canRetry(outcome) ? "Try again" : "Add to Google"}
            </button>
            {!isService && (
              <button type="button" disabled={busy} onClick={() => setEditing(s.row.code)} className={BTN_SECONDARY}>
                {source === "typical" ? "Set your hours" : value ? "Edit" : "Add yours"}
              </button>
            )}
            <span className="flex-1" />
            {back()}
            {at < slides.length - 1 && (
              <button type="button" onClick={() => go(at + 1)} className={BTN_QUIET}>
                Skip
              </button>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  /** Add / Try again / Skip, the same row every fix card ends with. */
  const actionRow = (item: ApplyItem | undefined, outcome: EditOutcome | null, extra?: React.ReactNode) => {
    const applying = outcome?.status === "applying";
    if (isSettled(outcome)) {
      return (
        <div className="flex items-center">
          <span className="flex-1" />
          {back()}
          {at < slides.length - 1 && (
            <button type="button" onClick={() => go(at + 1)} className={BTN_SECONDARY}>
              Next
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!item || busy || !autoOff || !onApply} onClick={() => item && onApply?.([item])} className={BTN_PRIMARY}>
          {applying ? "Sending…" : canRetry(outcome) ? "Try again" : "Add to Google"}
        </button>
        {extra}
        <span className="flex-1" />
        {back()}
        {at < slides.length - 1 && (
          <button type="button" onClick={() => go(at + 1)} className={BTN_QUIET}>
            Skip
          </button>
        )}
      </div>
    );
  };

  /* ── the main category: optional, never claimed to be wrong ── */

  const renderPrimary = (row: PrimaryRow) => {
    const outcome = row.outcome;
    const isEditing = editing === row.key;
    const headingId = `${domId(row.key)}-title`;
    // The audit's main category, as it was when the card was made.
    const before = row.current.display_name;
    return (
      <div className={`${CARD} grid gap-5`} aria-labelledby={headingId}>
        <div>
          <p className={KICKER}>Optional</p>
          <h3 id={headingId} className={`mt-1 ${TITLE}`}>
            Change main category
          </h3>
          <p className={EXPLAIN}>
            Your main category tells Google what you mainly do. Change it only if “{row.current.display_name}” doesn't describe
            your business.
          </p>
        </div>
        <dl className={TABLE}>
          <div className="grid gap-1.5 px-4 py-3.5 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-4 sm:px-[18px] sm:py-4">
            <dt className="text-[13px] text-theme-fg-muted">Now</dt>
            <dd className="min-w-0 break-words text-[14.5px] text-theme-fg-muted">{before}</dd>
          </div>
          <div className="grid gap-1.5 border-t border-theme-border px-4 py-3.5 sm:grid-cols-[104px_minmax(0,1fr)] sm:gap-4 sm:px-[18px] sm:py-4">
            <dt className="text-[13px] font-semibold text-theme-fg">{outcome?.status === "live" ? "On Google" : "Change to"}</dt>
            <dd className="grid min-w-0 gap-1.5 text-[14.5px] font-medium text-theme-fg">
              {isEditing ? (
                <CategoryEditor
                  single
                  row={{ value: row.value ? [row.value] : null, spec: { title: "Main category", kind: "categories", placeholder: "Search Google's categories", maxLength: 80 } }}
                  search={searchCategories}
                  onSave={(v) => {
                    const [picked] = Array.isArray(v) ? (v as GbpCategoryOption[]) : [];
                    if (picked) update({ ...card, primaryAnswer: picked });
                    setEditing(null);
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : row.value ? (
                <>
                  <span className="break-words">{row.value.display_name}</span>
                  <span className="text-[12.5px] font-normal text-theme-fg-muted">{SOURCE_LABELS.owner}</span>
                </>
              ) : (
                <span className="font-normal text-theme-fg-muted">Nothing chosen. Keep it as it is, or pick one from Google's list.</span>
              )}
            </dd>
          </div>
        </dl>
        <OutcomeLine outcome={outcome} />
        <WarningLine outcome={outcome} />
        {!isEditing &&
          actionRow(
            itemFor(row.key),
            outcome,
            !isSettled(outcome) && (
              <button type="button" disabled={busy || !searchCategories} onClick={() => setEditing(row.key)} className={BTN_SECONDARY}>
                {row.value ? "Edit" : "Choose a category"}
              </button>
            ),
          )}
      </div>
    );
  };

  /* ── attributes: one per Add ── */

  const chooseAttribute = (id: string, value: boolean | string) =>
    update({ ...card, attributeAnswers: { ...(card.attributeAnswers ?? {}), [id]: value } });

  const renderAttributes = (rows: AttributeRow[]) => (
    <div className={`${CARD} grid gap-5`}>
      <div>
        <p className={KICKER}>Business details</p>
        <h3 className={`mt-1 ${TITLE}`}>Add business details</h3>
        <p className={EXPLAIN}>
          Google offers these details for your kind of business, and your profile doesn't answer them yet. Pick the ones that are
          true; each is sent on its own.
        </p>
      </div>
      <ul className={`${TABLE} max-h-[420px] overflow-y-auto`}>
        {rows.map((r, k) => {
          const item = itemFor(r.key);
          const settled = isSettled(r.outcome);
          const applying = r.outcome?.status === "applying";
          const name = r.option.display_name || r.option.id;
          const labelId = `${domId(r.key)}-name`;
          return (
            <li key={r.key} className={`grid gap-2 px-4 py-3.5 sm:px-[18px] ${k ? "border-t border-theme-border" : ""}`}>
              <span className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <span className="min-w-0">
                  <span id={labelId} className="block text-[14.5px] font-semibold text-theme-fg">
                    {name}
                  </span>
                  {r.option.group && <span className="block text-[12.5px] text-theme-fg-muted">{r.option.group}</span>}
                </span>
                {settled ? (
                  <span className="text-[13.5px] text-theme-fg">{attributeValueLabel(r.option, r.value)}</span>
                ) : (
                  <span className="flex flex-wrap items-center gap-2">
                    {r.option.value_type === "BOOL" ? (
                      <span role="group" aria-labelledby={labelId} className="flex gap-1.5">
                        {[true, false].map((v) => (
                          <button
                            key={String(v)}
                            type="button"
                            aria-pressed={r.value === v}
                            disabled={busy}
                            onClick={() => chooseAttribute(r.option.id, v)}
                            className={`h-9 rounded-[10px] border px-3 text-[13.5px] font-semibold transition-colors ${
                              r.value === v
                                ? "border-theme-fg bg-theme-fg text-theme-surface"
                                : "border-theme-border bg-theme-surface text-theme-fg hover:border-theme-fg-muted"
                            }`}
                          >
                            {v ? "Yes" : "No"}
                          </button>
                        ))}
                      </span>
                    ) : (
                      <select
                        aria-labelledby={labelId}
                        value={typeof r.value === "string" ? r.value : ""}
                        disabled={busy}
                        onChange={(e) => e.target.value && chooseAttribute(r.option.id, e.target.value)}
                        className={SELECT_SMALL}
                      >
                        <option value="">Choose…</option>
                        {r.option.options
                          .filter((o) => typeof o.value === "string")
                          .map((o) => (
                            <option key={String(o.value)} value={String(o.value)}>
                              {o.display_name || String(o.value)}
                            </option>
                          ))}
                      </select>
                    )}
                    <button
                      type="button"
                      aria-label={`Add ${name} to Google`}
                      disabled={!item || busy || !autoOff || !onApply}
                      onClick={() => item && onApply?.([item])}
                      className={`${BTN_PRIMARY} h-9`}
                    >
                      {applying ? "Sending…" : canRetry(r.outcome) ? "Try again" : "Add"}
                    </button>
                  </span>
                )}
              </span>
              <OutcomeLine outcome={r.outcome} />
            </li>
          );
        })}
      </ul>
      {attributesNotEditable > 0 && (
        <p className="text-[13px] text-theme-fg-muted">
          {attributesNotEditable === 1 ? "1 more can" : `${attributesNotEditable} more can`} only be changed on Google.
        </p>
      )}
      {navRow()}
    </div>
  );

  /* ── a photo from the owner's Media Library ── */

  const openLibrary = () => {
    if (!loadMediaLibrary) return;
    const seq = ++librarySeq.current;
    setLibrary({ ...LIBRARY_CLOSED, open: true, loading: true });
    loadMediaLibrary(null)
      .then((page) => {
        if (seq === librarySeq.current) setLibrary({ ...LIBRARY_CLOSED, open: true, items: page.items, nextToken: page.nextToken });
      })
      .catch(() => {
        if (seq === librarySeq.current) setLibrary({ ...LIBRARY_CLOSED, failed: true });
      });
  };
  const loadMoreLibrary = () => {
    const token = library.nextToken;
    if (!loadMediaLibrary || !token || library.loadingMore) return;
    const seq = librarySeq.current;
    setLibrary((l) => ({ ...l, loadingMore: true, moreFailed: false }));
    loadMediaLibrary(token)
      .then((page) => {
        if (seq !== librarySeq.current) return;
        setLibrary((l) => {
          const seen = new Set(l.items.map((m) => m.media_id));
          const fresh = page.items.filter((m) => !seen.has(m.media_id));
          return { ...l, loadingMore: false, items: [...l.items, ...fresh], nextToken: page.nextToken };
        });
      })
      .catch(() => {
        if (seq === librarySeq.current) setLibrary((l) => ({ ...l, loadingMore: false, moreFailed: true }));
      });
  };
  const closeLibrary = () => {
    librarySeq.current++;
    setLibrary((l) => ({ ...LIBRARY_CLOSED, failed: l.failed }));
  };
  const setPhoto = (next: { sourceUrl?: string; category?: GbpPhotoCategory | null }) =>
    update({
      ...card,
      photo: {
        sourceUrl: next.sourceUrl ?? card.photo?.sourceUrl ?? "",
        category: next.category !== undefined ? next.category : card.photo?.category ?? null,
      },
    });

  const renderPhoto = (row: PhotoRow) => {
    const outcome = row.outcome;
    const settled = isSettled(outcome);
    const pick = row.value;
    const headingId = `${domId(row.key)}-title`;
    const categoryId = `${domId(row.key)}-category`;
    return (
      <div className={`${CARD} grid gap-5`} aria-labelledby={headingId}>
        <div>
          <p className={KICKER}>Photos</p>
          <h3 id={headingId} className={`mt-1 ${TITLE}`}>
            Add a photo
          </h3>
          <p className={EXPLAIN}>Your profile has no photos. Pick one of your own from your Media Library.</p>
        </div>
        {pick?.sourceUrl ? (
          <img src={pick.sourceUrl} alt="Your pick from the Media Library" className="max-h-44 w-auto max-w-full justify-self-start rounded-[10px] border border-theme-border object-contain" />
        ) : (
          <p className="text-[14px] text-theme-fg-muted">No photo picked yet.</p>
        )}
        {library.failed && <p className="text-[13.5px] text-theme-fg">I couldn't open your Media Library just now. Try again in a moment.</p>}
        {!settled && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={busy || !loadMediaLibrary} onClick={openLibrary} className={BTN_SECONDARY}>
              {pick?.sourceUrl ? "Choose another" : "Choose from your library"}
            </button>
            <label htmlFor={categoryId} className="sr-only">
              What the photo shows
            </label>
            <select
              id={categoryId}
              value={pick?.category ?? ""}
              disabled={busy || !pick?.sourceUrl}
              onChange={(e) => setPhoto({ category: (e.target.value || null) as GbpPhotoCategory | null })}
              className={`${INPUT} w-auto min-w-[200px] font-normal`}
            >
              <option value="">What does it show?</option>
              {GBP_PHOTO_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {settled && pick?.category && <p className="text-[13.5px] text-theme-fg-muted">Filed as: {photoCategoryLabel(pick.category)}</p>}
        <OutcomeLine outcome={outcome} />
        {actionRow(itemFor(row.key), outcome)}
      </div>
    );
  };

  /* ── the other cards ── */

  const renderSetup = () =>
    autoOff ? (
      <div className={`${CARD} grid gap-5`}>
        <div>
          <p className={KICKER}>Before we start</p>
          <h3 className={`mt-1 ${TITLE}`}>Google's automatic changes are off</h3>
          <p className={EXPLAIN}>Only the changes you approve will reach your profile now.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {slides.length > 1 && (
            <button type="button" onClick={() => go(1)} className={BTN_PRIMARY}>
              See the first fix
            </button>
          )}
          <span className="flex-1" />
          {!busy && (
            <button type="button" onClick={() => update({ ...card, googleAutoOff: false })} className={BTN_QUIET}>
              I haven't done this
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className={`${CARD} grid gap-5`}>
        <div>
          <p className={KICKER}>Before we start</p>
          <h3 className={`mt-1 ${TITLE}`}>Turn off two Google settings</h3>
          <p className={EXPLAIN}>
            Google can change your profile on its own, and that can undo the fixes you approve here. Switch these off first.
          </p>
        </div>
        <ul className={TABLE}>
          {["Keep your profile up to date", "Post to your profile on your behalf"].map((name, k) => (
            <li
              key={name}
              className={`flex items-center justify-between gap-4 px-4 py-3.5 text-[14.5px] text-theme-fg sm:px-[18px] ${k ? "border-t border-theme-border" : ""}`}
            >
              <span>{name}</span>
              <span className="flex flex-shrink-0 items-center gap-2.5 text-[13px] text-theme-fg-muted">
                Off
                <span aria-hidden="true" className="relative h-[18px] w-8 rounded-full bg-theme-border">
                  <span className="absolute left-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-theme-surface" />
                </span>
              </span>
            </li>
          ))}
        </ul>
        <ol className="grid gap-1.5 text-[14px] text-theme-fg-muted">
          <li>1. Search Google for your business while signed in.</li>
          <li>
            2. Click <strong className="font-semibold text-theme-fg">⋮</strong>, then{" "}
            <strong className="font-semibold text-theme-fg">Business Profile settings</strong>.
          </li>
          <li>
            3. Open <strong className="font-semibold text-theme-fg">Advanced settings</strong> and switch both off.
          </li>
        </ol>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => update({ ...card, googleAutoOff: true })} className={BTN_PRIMARY}>
            I've turned them off
          </button>
          <span className="flex-1" />
          <span className="text-[12.5px] text-theme-fg-muted">Needed before the fixes.</span>
        </div>
      </div>
    );

  const renderManual = (rows: AskRow[]) => (
    <div className={`${CARD} grid gap-5`}>
      <div>
        <p className={KICKER}>Add these yourself</p>
        <h3 className={`mt-1 ${TITLE}`}>
          {rows.length === 1 ? "One thing only you can add" : `${plural(rows.length, "thing", "things")} only you can add`}
        </h3>
        <p className={EXPLAIN}>I can't set these yet. Add them on your Business Profile on Google.</p>
      </div>
      <ul className={TABLE}>
        {rows.map((r, k) => (
          <li key={r.key} className={`grid gap-0.5 px-4 py-3.5 sm:px-[18px] ${k ? "border-t border-theme-border" : ""}`}>
            <span className="flex items-center justify-between gap-4">
              <span className="text-[14.5px] font-semibold text-theme-fg">{r.spec.title}</span>
              <span className="flex flex-shrink-0 items-center gap-2 text-[13px] text-theme-fg-muted">
                <Dot tone="none" />
                Missing
              </span>
            </span>
            {r.value && (
              <span className="text-[13.5px] text-theme-fg">
                Suggested: {formatValue(r.value)}
                <span className="text-theme-fg-muted"> · {SOURCE_LABELS[r.source ?? "owner"]}</span>
              </span>
            )}
            {WHERE_BY_GAP[r.code] && <span className="text-[13px] text-theme-fg-muted">{WHERE_BY_GAP[r.code]}</span>}
          </li>
        ))}
      </ul>
      {navRow()}
    </div>
  );

  const renderTeam = (rows: HandOffRow[]) => (
    <div className={`${CARD} grid gap-5`}>
      <div>
        <p className={KICKER}>For your team</p>
        <h3 className={`mt-1 ${TITLE}`}>{rows.length === 1 ? rows[0].spec.title : "Work for your team"}</h3>
        <p className={EXPLAIN}>{handOffExplain(rows)}</p>
      </div>
      <ul className={TABLE}>
        {rows.map((h, k) => (
          <li
            key={`handoff:${h.code}`}
            className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:px-[18px] ${k ? "border-t border-theme-border" : ""}`}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[14.5px] font-semibold text-theme-fg">{h.spec.title}</span>
              <span className="block text-[13px] text-theme-fg-muted">{h.detail}</span>
            </span>
            {h.member ? (
              <button type="button" onClick={() => onHandOff?.(h.member!.id, h.spec.prompt)} disabled={!onHandOff} className={BTN_SECONDARY}>
                Ask {h.member.name}
              </button>
            ) : (
              <span className="text-[13px] text-theme-fg-muted">No {h.spec.roleLabel} yet</span>
            )}
          </li>
        ))}
      </ul>
      {navRow()}
    </div>
  );

  // Photo, attributes and main category in the summary: the photo always (it is
  // a fix), attributes and the main category only once the owner sent them.
  const extraSummary: Array<{ key: string; field: string; outcome: EditOutcome | null; change?: string }> = [
    ...(photo ? [{ key: photo.key, field: "Photo", outcome: photo.outcome, change: photo.value?.category ? `Added: ${photoCategoryLabel(photo.value.category)}` : undefined }] : []),
    ...attributes
      .filter((r) => r.outcome)
      .map((r) => ({
        key: r.key,
        field: r.option.display_name || r.option.id,
        outcome: r.outcome,
        change: `Not set → ${attributeValueLabel(r.option, r.value)}`,
      })),
    ...(primary?.outcome
      ? [{ key: primary.key, field: "Main category", outcome: primary.outcome, change: `${primary.current.display_name} → ${primary.value?.display_name ?? ""}` }]
      : []),
  ];

  const renderSummary = () => (
    <div className={`${CARD} grid gap-5`}>
      <div>
        <p className={KICKER}>Summary</p>
        {progress.total === 0 || onlyUnanswered ? (
          <h3 className={`mt-1 ${TITLE}`}>{unanswered ? "No answer from Google yet" : "Nothing changed yet"}</h3>
        ) : allLive ? (
          <>
            <h3 className={`mt-1 flex items-center gap-2.5 ${TITLE}`}>
              <span
                aria-hidden="true"
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-theme-primary text-white"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={3}>
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </span>
              All done. Your profile is updated.
            </h3>
            <p className={EXPLAIN}>
              {progress.total === 1 ? "Your change is live on Google." : `All ${progress.total} changes are live on Google.`}
            </p>
          </>
        ) : (
          <>
            <h3 className={`mt-1 ${TITLE}`}>
              {progress.live} of {plural(progress.total, "change", "changes")} live on Google
            </h3>
            <p className={EXPLAIN}>
              {progress.waiting > 0 &&
                "The rest are waiting for Google to publish them. This usually takes a few hours to a few days. "}
              {progress.missing > 0 && "Some changes aren't showing on Google. Go back to them to try again."}
            </p>
          </>
        )}
      </div>
      {fixes.length > 0 && (
        <ul className={TABLE}>
          {fixes.map((s, k) => {
            const o = s.row.outcome;
            const isService = s.kind === "service";
            const field = isService ? `Service: ${s.row.group.service}` : s.row.spec.title;
            const value = isService ? s.row.group.service : s.row.value ? formatValue(s.row.value) : "";
            const shown = value.length > 70 ? `${value.slice(0, 70)}…` : value;
            const old = o?.old != null && formatValue(o.old) ? formatValue(o.old) : "Missing";
            const [tone, word] = statusWord(o);
            return (
              <li key={s.key} className={`grid gap-0.5 px-4 py-3.5 sm:px-[18px] ${k ? "border-t border-theme-border" : ""}`}>
                <span className="flex items-center justify-between gap-4">
                  <span className="min-w-0 truncate text-[14.5px] font-semibold text-theme-fg">{field}</span>
                  <span className="flex flex-shrink-0 items-center gap-2 text-[13px] text-theme-fg-muted">
                    <Dot tone={tone} />
                    {word}
                  </span>
                </span>
                {isSettled(o) && (
                  <span className="break-words text-[13.5px] text-theme-fg-muted">
                    <s>{old}</s> → {shown}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {extraSummary.length > 0 && (
        <ul className={TABLE}>
          {extraSummary.map((e, k) => {
            const [tone, word] = statusWord(e.outcome);
            return (
              <li key={e.key} className={`grid gap-0.5 px-4 py-3.5 sm:px-[18px] ${k ? "border-t border-theme-border" : ""}`}>
                <span className="flex items-center justify-between gap-4">
                  <span className="min-w-0 truncate text-[14.5px] font-semibold text-theme-fg">{e.field}</span>
                  <span className="flex flex-shrink-0 items-center gap-2 text-[13px] text-theme-fg-muted">
                    <Dot tone={tone} />
                    {word}
                  </span>
                </span>
                {isSettled(e.outcome) && e.change && <span className="break-words text-[13.5px] text-theme-fg-muted">{e.change}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {notes.length > 0 && (
        <p className="text-[13px] text-theme-fg-muted">Also on your profile: {notes.map((n) => n.title.toLowerCase()).join(", ")}.</p>
      )}
      {auditProblem(card.audit) && <p className="text-[13px] text-theme-fg-muted">{auditProblem(card.audit)}</p>}
      {gbpConnectLabel(card) && onConnect && (
        <div className="flex">
          <button type="button" onClick={onConnect} className={BTN_SECONDARY}>
            {gbpConnectLabel(card)}
          </button>
        </div>
      )}
      {progress.total > 0 && !allLive && checkStatus && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-theme-fg-muted">
          <button type="button" disabled={checking} onClick={() => void runCheck(true)} className={BTN_SECONDARY}>
            {checking ? "Checking…" : "Check again"}
          </button>
          <span role="status">
            {checkFailed
              ? "Couldn't reach Google just now."
              : checkedAgo(lastChecked)
                ? `Checked ${checkedAgo(lastChecked)}. I'll keep checking while this is open.`
                : "I'll keep checking while this is open."}
          </span>
        </div>
      )}
      {(sent === 0 || at > 0) && (
        <div className="flex items-center gap-2">
          <span className="flex-1" />
          {back()}
          {sent === 0 && (
            <button type="button" disabled={busy} onClick={() => update({ ...card, dismissed: true })} className={BTN_SECONDARY}>
              Not now
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderSlide = (s: Slide) => {
    switch (s.kind) {
      case "setup":
        return renderSetup();
      case "service":
      case "ask":
        return renderFix(s);
      case "primary":
        return renderPrimary(s.row);
      case "attributes":
        return renderAttributes(s.rows);
      case "photo":
        return renderPhoto(s.row);
      case "manual":
        return renderManual(s.rows);
      case "team":
        return renderTeam(s.rows);
      default:
        return renderSummary();
    }
  };

  const canPrev = at > 0;
  const canNext = at < maxIndex;
  const onKeyDown = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (e.key === "ArrowRight" && canNext) go(at + 1);
    if (e.key === "ArrowLeft" && canPrev) go(at - 1);
  };

  return (
    <section ref={rootRef} aria-label={label} className="flex w-full scroll-mt-6 gap-3 text-left" onKeyDown={onKeyDown}>
      {avatar}
      <div className="min-w-0 flex-1 space-y-4">
        <p className="max-w-[60ch] text-[14px] leading-relaxed text-theme-fg">{lead}</p>

        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[13px] font-semibold text-theme-fg">Google Business Profile</span>
            <span className="text-[13px] tabular-nums text-theme-fg-muted">{progress.total === 0 || onlyUnanswered
                ? unanswered
                  ? "No answer yet"
                  : "Nothing changed yet"
                : allLive
                  ? "All live on Google"
                  : `${progress.live} of ${progress.total} live`}
            </span>
          </div>
          <div className="h-0.5 overflow-hidden rounded-full bg-theme-border">
            <div
              className="h-full rounded-full bg-theme-fg motion-reduce:transition-none"
              style={{ width: `${((at + 1) / slides.length) * 100}%`, transition: `width ${SLIDE_MS}ms ${EASE}` }}
            />
          </div>

          <div className="flex items-center">
            <div
              className="-my-3 min-w-0 flex-1 overflow-hidden py-3 motion-reduce:transition-none"
              style={{ height: height !== undefined ? height + 24 : undefined, transition: `height ${SLIDE_MS}ms ${EASE}` }}
              onTouchStart={(e) => {
                touchX.current = e.touches[0].clientX;
              }}
              onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                touchX.current = null;
                if (dx < -50 && canNext) go(at + 1);
                if (dx > 50 && canPrev) go(at - 1);
              }}
            >
              <div
                className="flex items-start motion-reduce:transition-none"
                style={{
                  gap: SLIDE_GAP,
                  transform: `translateX(calc(${-at * 100}% - ${at * SLIDE_GAP}px))`,
                  transition: `transform ${SLIDE_MS}ms ${EASE}`,
                }}
              >
                {slides.map((s, k) => (
                  <div
                    key={s.key}
                    ref={(el) => {
                      slideRefs.current[k] = el;
                    }}
                    aria-hidden={k !== at}
                    className="w-full min-w-0 flex-shrink-0 motion-reduce:transition-none"
                    style={{ opacity: k === at ? 1 : 0.35, transition: `opacity ${SLIDE_MS}ms ${EASE}` }}
                  >
                    {renderSlide(s)}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
      {/* The picker is a full-screen overlay. Rendered here it would sit inside the
          deck, whose transform and overflow-hidden turn `fixed` into "fixed to the
          deck" and clip it, so it goes to document.body. React still bubbles its
          events through this tree, so the wrapper keeps its clicks and keys from
          reaching the card (the arrow keys would change the card). It sits outside
          the swipe area, so touches never reach the deck's swipe handlers. */}
      {library.open &&
        createPortal(
          <div onClick={stopPropagation} onKeyDown={stopPropagation}>
            <MediaLibraryModal
              isOpen
              onClose={closeLibrary}
              mediaItems={library.items}
              selectedImages={[]}
              onImageSelect={(item) => {
                const url = getMediaThumbnailUrl(item);
                if (url) setPhoto({ sourceUrl: url });
              }}
              loading={library.loading}
              mediaTypeFilter="image"
              subtitle="Choose one photo for your Google Business Profile."
              emptyMessage={
                <p>
                  {library.nextToken
                    ? "No photos you can use in this part of your library. Load more to keep looking."
                    : "No photos you can use yet. Add one in your Media Library, then come back here."}
                </p>
              }
              onLoadMore={loadMoreLibrary}
              hasMore={!!library.nextToken}
              loadingMore={library.loadingMore}
              loadMoreFailed={library.moreFailed}
            />
          </div>,
          document.body,
        )}
    </section>
  );
};
